import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import googleMeetService from '../services/googleMeetService';
import { useAuth } from './AuthContext';
import { normalizeRole, isExternalRole } from '../services/roleManagementService';

const CALENDAR_SCOPE = 'https://www.googleapis.com/auth/calendar.events';

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const isConfigured = Boolean(googleClientId && googleClientId !== 'your_google_client_id_here');

if (!isConfigured) {
  console.warn('⚠️ Google Meet Integration: REACT_APP_GOOGLE_CLIENT_ID not configured');
}

const GoogleMeetContext = createContext();

// Stub value used when Google is not configured — same shape as the real value.
const STUB_VALUE = {
  isGoogleAuthenticated: false,
  signInWithGoogle: () => ({ success: false, error: 'REACT_APP_GOOGLE_CLIENT_ID is not configured.' }),
  signOutFromGoogle: () => ({ success: true }),
  accessToken: null,
  loading: false,
  isConfigured: false,
  googleMeetService: null,
  configurationStatus: 'needs-setup',
};

// ---------------------------------------------------------------------------
// Inner component — MUST be mounted inside GoogleOAuthProvider.
// Only rendered when isConfigured is true.
// ---------------------------------------------------------------------------
const GoogleMeetCore = ({ children }) => {
  const { userProfile } = useAuth();
  const [isGoogleAuthenticated, setIsGoogleAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const [loading, setLoading] = useState(false);

  const role = normalizeRole(userProfile?.role);
  const allowGoogleConnect =
    Boolean(role) &&
    ['admin', 'facilitator', 'manager', 'qa'].includes(role) &&
    !isExternalRole(role);

  useEffect(() => {
    if (!allowGoogleConnect) {
      setIsGoogleAuthenticated(false);
      setAccessToken(null);
    }
  }, [allowGoogleConnect]);

  const handleGoogleSuccess = useCallback(async (tokenResponse) => {
    setLoading(true);
    try {
      const token = tokenResponse.access_token;
      setAccessToken(token);
      setIsGoogleAuthenticated(true);
      googleMeetService.initializeClient(token);
      console.log('✅ Google Meet: signed in and service initialised');
    } catch (err) {
      console.error('❌ Google Meet initialisation error:', err);
      setIsGoogleAuthenticated(false);
      setAccessToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // useGoogleLogin is safe here — GoogleMeetCore is only rendered inside GoogleOAuthProvider
  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: (err) => {
      console.error('❌ Google OAuth error:', err);
      setLoading(false);
    },
    scope: CALENDAR_SCOPE,
    flow: 'implicit',
  });

  const signInWithGoogle = useCallback(() => {
    if (!allowGoogleConnect) {
      return { success: false, error: 'Google Meet connection is restricted to internal users.' };
    }
    setLoading(true);
    googleLogin();
    return { success: true };
  }, [allowGoogleConnect, googleLogin]);

  const signOutFromGoogle = useCallback(() => {
    setIsGoogleAuthenticated(false);
    setAccessToken(null);
    googleMeetService.clearClient();
    console.log('Google Meet: signed out');
    return { success: true };
  }, []);

  const value = {
    isGoogleAuthenticated,
    signInWithGoogle,
    signOutFromGoogle,
    accessToken,
    loading,
    isConfigured: true,
    googleMeetService: isGoogleAuthenticated ? googleMeetService : null,
    configurationStatus: isGoogleAuthenticated ? 'ready' : 'awaiting-login',
  };

  return (
    <GoogleMeetContext.Provider value={value}>
      {children}
    </GoogleMeetContext.Provider>
  );
};

// ---------------------------------------------------------------------------
// Public provider — safe to mount anywhere, with or without GoogleOAuthProvider.
// ---------------------------------------------------------------------------
export const GoogleMeetProvider = ({ children }) => {
  if (!isConfigured) {
    return (
      <GoogleMeetContext.Provider value={STUB_VALUE}>
        {children}
      </GoogleMeetContext.Provider>
    );
  }
  return <GoogleMeetCore>{children}</GoogleMeetCore>;
};

export const useGoogleMeet = () => {
  const context = useContext(GoogleMeetContext);
  if (!context) {
    throw new Error('useGoogleMeet must be used within a GoogleMeetProvider');
  }
  return context;
};

export default GoogleMeetProvider;
